import { useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { smsLink, mailLink, telLink } from "../lib/helpers";
import { Field, useBusy } from "./common";

export default function MessageDialog({ customer, projectId, followupId, defaultType = "sms", onClose, onSent, onSendFollowup }) {
  const { t } = useLang();
  const [type, setType] = useState(defaultType);
  const [text, setText] = useState("");
  const [subject, setSubject] = useState("");
  const [busy, run] = useBusy();
  const [aiBusy, runAi] = useBusy();
  const draft = () =>
    runAi(async () => {
      try {
        const { data } = await api.post("/ai/followup-message", followupId ? { followup_id: followupId } : { customer_id: customer.id });
        setText(data.message);
      } catch (e) {
        toast.error(errMsg(e));
      }
    });
  const send = () =>
    run(async () => {
      if (!text.trim()) return toast.error(t("Write a message", "Escriba un mensaje"));
      try {
        if (onSendFollowup) await onSendFollowup(text, type);
        else await api.post("/communications", { customer_id: customer.id, type, channel: type, subject, content: text, project_id: projectId || null });
        if (type === "sms") window.location.href = smsLink(customer.phone, text);
        if (type === "email") window.location.href = mailLink(customer.email, subject, text);
        if (type === "call") window.location.href = telLink(customer.phone);
        toast.success(t("Saved to communication history", "Guardado en el historial"));
        onSent?.();
        onClose();
      } catch (e) {
        toast.error(errMsg(e));
      }
    });
  const types = onSendFollowup ? [["sms", "SMS"], ["email", t("Email", "Correo")]] : [["sms", "SMS"], ["email", t("Email", "Correo")], ["call", t("Call", "Llamada")], ["note", t("Note", "Nota")]];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="message-dialog">
        <DialogHeader>
          <DialogTitle>{t("Message", "Mensaje")} · {customer.first_name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-4 gap-1 rounded-xl bg-slate-100 p-1">
          {types.map(([k, l]) => (
            <button key={k} onClick={() => setType(k)} data-testid={`message-type-${k}`}
              className={`min-h-[40px] rounded-lg text-sm font-semibold ${type === k ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
        {type === "email" && (
          <Field label={t("Subject", "Asunto")}>
            <input className="inp" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="message-subject-input" />
          </Field>
        )}
        <Field label={type === "call" ? t("Call notes", "Notas de la llamada") : t("Message", "Mensaje")}>
          <textarea className="inp min-h-[140px]" value={text} onChange={(e) => setText(e.target.value)} data-testid="message-text-input" />
        </Field>
        {type !== "note" && type !== "call" && (
          <button className="btn-outline" onClick={draft} disabled={aiBusy} data-testid="message-ai-draft-btn">
            <Sparkles className="w-5 h-5 text-[#D9531E]" /> {aiBusy ? t("Writing…", "Escribiendo…") : t("AI: write follow-up message", "IA: escribir mensaje de seguimiento")}
          </button>
        )}
        <button className="btn-primary" onClick={send} disabled={busy} data-testid="message-send-btn">
          {type === "note" ? t("Save note", "Guardar nota") : type === "call" ? t("Log & call", "Registrar y llamar") : t("Save & open to send", "Guardar y abrir para enviar")}
        </button>
      </DialogContent>
    </Dialog>
  );
}
